import { useCallback, useMemo, useContext, createContext, ReactNode } from "react"
import { StringifiableRecord, ParsedQuery, parse, stringify } from "query-string"
import { useNavigate, useLocation } from "react-router-dom"
import { useAutoUpdatedRef } from "@oysterhr/common"

export interface UrlQueryParamContextProps {
  params: ParsedQuery<string>
  hash: Location["hash"]
  setParam: (key: string, value: string | undefined) => void
  setParams: (params: StringifiableRecord) => void
  setParamMulti: (params: StringifiableRecord) => void
  getStringParams: () => string
}

const throwNoContextError = () => {
  throw new Error("UrlQueryParamContext not provided")
}

export const UrlQueryParamContext = createContext<UrlQueryParamContextProps>({
  params: {},
  hash: "",
  setParam: throwNoContextError,
  setParams: throwNoContextError,
  setParamMulti: throwNoContextError,
  getStringParams: throwNoContextError,
})

interface UrlQueryParamProviderProps {
  children: ReactNode
}

export const UrlQueryParamProvider = ({ children }: UrlQueryParamProviderProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const localParams = useMemo(() => parse(location.search, { sort: false }), [location.search])
  const eventDataRef = useAutoUpdatedRef({
    hash: location.hash,
    localParams,
  })

  const setParamMulti: UrlQueryParamContextProps["setParamMulti"] = params => {
    const newParams = { ...eventDataRef.current.localParams, ...params }
    Object.keys(params).forEach(key => {
      if (params[key] === undefined) {
        const { [key]: _, ...rest } = newParams
        Object.assign(newParams, rest)
      }
    })
    setParams(newParams)
  }

  const setParams: UrlQueryParamContextProps["setParams"] = useCallback(
    params => {
      const searchString = stringify(params, { skipEmptyString: true, sort: false })
      navigate(`${location.pathname}?${searchString}${eventDataRef.current.hash}`)
    },
    [navigate, location.pathname, eventDataRef],
  )

  const setParam: UrlQueryParamContextProps["setParam"] = useCallback(
    (name, value) => {
      const { [name]: _, ...rest } = eventDataRef.current.localParams
      const newParams = value === undefined ? rest : { ...rest, [name]: value }
      setParams(newParams)
    },
    [setParams, eventDataRef],
  )

  const getStringParams = () => {
    return stringify(eventDataRef.current.localParams, { sort: false })
  }

  return (
    <UrlQueryParamContext.Provider
      value={{
        params: localParams,
        hash: location.hash,
        setParam,
        setParamMulti,
        setParams,
        getStringParams,
      }}
    >
      {children}
    </UrlQueryParamContext.Provider>
  )
}

export const useUrlQueryParamContext = () => useContext(UrlQueryParamContext)
